import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Stack, Tab, Tabs,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, CheckCircle as FinishIcon, Delete as DeleteIcon, Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import {
  DENTAL_ENTRY_STATUSES,
  formatFaces,
  getConditionColor,
  getConditionLabel,
  getDentalStatus,
  getToothName,
  type Dentition,
  type ToothFace,
} from '../../constants/odontology';
import type { DentalChart, DentalChartEntry, DentalEntryStatus } from '../../types/odontology';
import {
  deleteDentalChartEntry,
  getDentalChart,
  updateDentalChart,
  updateDentalChartEntry,
} from '../../services/odontologyService';
import { buildToothViewMap } from '../../utils/dentalChartView';
import DentalChartCanvas from './DentalChartCanvas';
import DentalChartEntryDialog from './DentalChartEntryDialog';
import { formatCurrencyBr, formatDateBr, todayIso } from './odontologyHooks';

export interface DentalChartPanelProps {
  patientId: string;
  unitId?: string | null;
  /** Compacta o cabeçalho quando embutido na aba do paciente. */
  dense?: boolean;
  /** Dentro da aba Odontograma (sub-aba mapa): oculta título duplicado. */
  embedded?: boolean;
}

type StatusTab = DentalEntryStatus | 'all';

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: 'all', label: 'Tudo' },
  { value: 'planned', label: 'A realizar' },
  { value: 'completed', label: 'Realizado' },
  { value: 'pre_existing', label: 'Pré-existente' },
  { value: 'in_progress', label: 'Em andamento' },
];

const DentalChartPanel: React.FC<DentalChartPanelProps> = ({ patientId, unitId, dense = false, embedded = false }) => {
  const [chart, setChart] = useState<DentalChart | null>(null);
  const [entries, setEntries] = useState<DentalChartEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dentition, setDentition] = useState<Dentition>('permanent');
  // O perfil lateral dobra a altura do odontograma, então fica ligado por
  // padrão só na tela cheia; na aba do paciente o espaço é mais disputado.
  const [showLateral, setShowLateral] = useState(!dense);
  const [selectedTooth, setSelectedTooth] = useState<string | null>(null);
  const [selectedFaces, setSelectedFaces] = useState<ToothFace[]>([]);
  const [statusTab, setStatusTab] = useState<StatusTab>('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<DentalChartEntry | null>(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getDentalChart(patientId, unitId);
      setChart(data?.chart || null);
      setEntries(data?.entries || []);
      if (data?.chart?.dentition && data.chart.dentition !== 'mixed') {
        setDentition(data.chart.dentition as Dentition);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao carregar odontograma');
    } finally {
      setLoading(false);
    }
  }, [patientId, unitId]);

  useEffect(() => { load(); }, [load]);

  // O odontograma sempre mostra o quadro completo; a aba de status filtra só a
  // lista de lançamentos abaixo dele.
  const viewMap = useMemo(() => buildToothViewMap(entries), [entries]);

  const visibleEntries = useMemo(() => {
    const filtered = statusTab === 'all' ? entries : entries.filter((e) => e.status === statusTab);
    return selectedTooth ? filtered.filter((e) => e.tooth_number === selectedTooth) : filtered;
  }, [entries, statusTab, selectedTooth]);

  const totals = useMemo(() => {
    const planned = entries.filter((e) => e.status === 'planned');
    return {
      planned: planned.length,
      plannedAmount: planned.reduce((sum, e) => sum + (Number(e.amount) || 0), 0),
      completed: entries.filter((e) => e.status === 'completed').length,
    };
  }, [entries]);

  const handleSelectTooth = (tooth: string) => {
    setSelectedTooth((prev) => (prev === tooth ? null : tooth));
    setSelectedFaces([]);
  };

  const handleToggleFace = (tooth: string, face: ToothFace) => {
    if (selectedTooth !== tooth) {
      setSelectedTooth(tooth);
      setSelectedFaces([face]);
      return;
    }
    setSelectedFaces((prev) => (prev.includes(face) ? prev.filter((f) => f !== face) : [...prev, face]));
  };

  const openNewEntry = () => {
    setEditingEntry(null);
    setDialogOpen(true);
  };

  const openEditEntry = (entry: DentalChartEntry) => {
    setEditingEntry(entry);
    setSelectedTooth(entry.tooth_number);
    setDialogOpen(true);
  };

  const handleDelete = async (entry: DentalChartEntry) => {
    if (!window.confirm('Remover este lançamento do odontograma?')) return;
    try {
      await deleteDentalChartEntry(entry.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao remover lançamento');
    }
  };

  const handleFinish = async (entry: DentalChartEntry) => {
    try {
      await updateDentalChartEntry(entry.id, {
        status: 'completed',
        performed_date: todayIso(),
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao finalizar lançamento');
    }
  };

  const handleDentitionChange = async (value: Dentition) => {
    setDentition(value);
    if (!chart?.id) return;
    try {
      const updated = await updateDentalChart(chart.id, { dentition: value });
      setChart(updated);
    } catch {
      /* A preferência de dentição é local até o primeiro save bem-sucedido. */
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: dense ? { xs: 1.5, sm: 2 } : { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: embedded ? 'flex-end' : 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {!embedded && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', letterSpacing: '-0.02em' }}>
              Odontograma
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Notação FDI · clique numa face para marcar, no número para o dente inteiro
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={dentition}
            onChange={(_, value) => value && handleDentitionChange(value)}
          >
            <ToggleButton value="permanent" sx={{ textTransform: 'none', px: 1.5 }}>Permanentes</ToggleButton>
            <ToggleButton value="deciduous" sx={{ textTransform: 'none', px: 1.5 }}>Decíduos</ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Mostra raiz, canal e prótese em perfil, além das faces">
            <ToggleButton
              value="lateral"
              size="small"
              selected={showLateral}
              onClick={() => setShowLateral((v) => !v)}
              sx={{ textTransform: 'none', px: 1.5 }}
            >
              Perfil lateral
            </ToggleButton>
          </Tooltip>

          {dense && (
            <Button
              size="small"
              variant="outlined"
              href={`/clinic/odontogram?patient=${patientId}`}
              startIcon={<OpenInNewIcon />}
              sx={{ textTransform: 'none', borderColor: '#0D9488', color: '#0D9488' }}
            >
              Tela cheia
            </Button>
          )}

          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openNewEntry}
            disabled={!selectedTooth}
            sx={{ textTransform: 'none', bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' } }}
          >
            {selectedTooth ? `Lançar procedimento · ${selectedTooth}` : 'Selecione um dente'}
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <DentalChartCanvas
        dentition={dentition}
        viewMap={viewMap}
        selectedTooth={selectedTooth}
        selectedFaces={selectedFaces}
        showLateral={showLateral}
        onSelectTooth={handleSelectTooth}
        onToggleFace={handleToggleFace}
      />

      <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }} useFlexGap>
        {selectedTooth && (
          <Chip
            size="small"
            label={`${selectedTooth} · ${getToothName(selectedTooth)}${selectedFaces.length ? ` · ${formatFaces(selectedFaces, selectedTooth)}` : ''}`}
            onDelete={() => { setSelectedTooth(null); setSelectedFaces([]); }}
            sx={{ fontWeight: 600, bgcolor: '#E0F2FE', color: '#0C4A6E' }}
          />
        )}
        <Chip size="small" variant="outlined" label={`A realizar: ${totals.planned}`} sx={{ color: '#B45309', borderColor: '#FCD34D' }} />
        <Chip size="small" variant="outlined" label={`Realizados: ${totals.completed}`} sx={{ color: '#15803D', borderColor: '#86EFAC' }} />
        {totals.plannedAmount > 0 && (
          <Chip size="small" variant="outlined" label={`Planejado: ${formatCurrencyBr(totals.plannedAmount)}`} sx={{ color: '#0F172A' }} />
        )}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Tabs
        value={statusTab}
        onChange={(_, value) => setStatusTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          mb: 1.5,
          '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' },
        }}
      >
        {STATUS_TABS.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>

      {!visibleEntries.length ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {selectedTooth
            ? `Nenhum lançamento no dente ${selectedTooth} com este filtro.`
            : 'Nenhum lançamento registrado. Selecione um dente no odontograma para começar.'}
        </Alert>
      ) : (
        <Stack spacing={1}>
          {visibleEntries.map((entry) => {
            const status = getDentalStatus(entry.status);
            const color = entry.color || getConditionColor(entry.condition_code);
            return (
              <Box
                key={entry.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid #E2E8F0',
                  bgcolor: '#FFFFFF',
                  transition: 'border-color 0.15s ease',
                  '&:hover': { borderColor: '#0D9488' },
                }}
              >
                <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 2, bgcolor: color, flexShrink: 0 }} />

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                    {entry.tooth_number ? `${entry.tooth_number} · ` : ''}
                    {entry.procedure_name || getConditionLabel(entry.condition_code)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                    {entry.faces?.length ? `${formatFaces(entry.faces, entry.tooth_number || undefined)} · ` : ''}
                    {formatDateBr(entry.performed_date || entry.planned_date || entry.created_at)}
                    {entry.professional_name ? ` · ${entry.professional_name}` : ''}
                    {Number(entry.amount) > 0 ? ` · ${formatCurrencyBr(entry.amount)}` : ''}
                  </Typography>
                  {entry.description && (
                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.5 }}>
                      {entry.description}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Chip
                    size="small"
                    label={status.label}
                    sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600, bgcolor: status.bg, color: status.color }}
                  />
                  {(entry.status === 'planned' || entry.status === 'in_progress') && (
                    <Tooltip title="Marcar como realizado">
                      <IconButton size="small" onClick={() => handleFinish(entry)} sx={{ color: '#16A34A' }} aria-label="Finalizar lançamento">
                        <FinishIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => openEditEntry(entry)} sx={{ color: '#0D9488' }} aria-label="Editar lançamento">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remover">
                    <IconButton size="small" onClick={() => handleDelete(entry)} sx={{ color: '#94A3B8' }} aria-label="Remover lançamento">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        Legenda de status
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ mt: 0.75, flexWrap: 'wrap' }} useFlexGap>
        {DENTAL_ENTRY_STATUSES.filter((s) => s.value !== 'cancelled').map((status) => (
          <Box key={status.value} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: status.color }} />
            <Typography variant="caption" sx={{ color: '#64748B' }}>{status.label}</Typography>
          </Box>
        ))}
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 16, height: 16, borderRadius: 0.5, border: '1px solid #94A3B8', bgcolor: '#64748B' }} />
          <Typography variant="caption" sx={{ color: '#64748B' }}>Preenchimento sólido: já executado</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.5,
              border: '1px solid #94A3B8',
              backgroundImage: 'repeating-linear-gradient(45deg, #64748B 0 2.5px, #FFFFFF 2.5px 5px)',
            }}
          />
          <Typography variant="caption" sx={{ color: '#64748B' }}>Hachurado: a realizar</Typography>
        </Box>
      </Stack>

      <DentalChartEntryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        patientId={patientId}
        unitId={unitId}
        chartId={chart?.id}
        toothNumber={editingEntry?.tooth_number || selectedTooth}
        initialFaces={editingEntry ? undefined : selectedFaces}
        entry={editingEntry}
        onSaved={load}
      />
    </Box>
  );
};

export default DentalChartPanel;
